import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {NotesActions, NotesState, StateApiNote, handleDeleteNotes} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import colors from 'app/utils/colorUtils';
import * as mapUtils from 'app/utils/mapUtils';

import {usePushNotification} from '../Notification';
import {ALL_FILTER_ID, FilterOption, getDefaultFilterOptions} from './alertFilterOptions';
import {useAlertPolicies} from './AlertPolicyProvider';
import {noteMatchesAlertPolicy} from './alertTypes';
import LookoutIcon from './LookoutIcon';

interface UseAlertReviewStateProps {
  feature: I.ImmutableOf<ApiFeature>;
  notesState: NotesState;
  notesActions: NotesActions;
  onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => void;
  zoomToGeometry: (geometry: geojson.GeoJSON) => unknown;
  onSelectNote: (noteId: number | string) => unknown;
  canModifyAlerts: boolean;
}

export const useAlertReviewState = ({
  feature,
  notesState,
  notesActions,
  onSelectImageRefs,
  zoomToGeometry,
  onSelectNote,
  canModifyAlerts,
}: UseAlertReviewStateProps) => {
  const [filterId, setFilterId] = React.useState(ALL_FILTER_ID);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [selectedNotes, setSelectedNotes] = React.useState<Set<number | string>>(new Set());
  const {alertPolicies} = useAlertPolicies();
  const pushNotification = usePushNotification();

  const availableNotes = notesState.notes.filter(
    (note) => !note.isArchived && note.noteType === 'alert'
  );
  // The notes endpoint already sorts the list; descending order only needs a reversal.
  if (sortDirection === 'desc') {
    availableNotes.reverse();
  }

  const filterOptions: FilterOption[] = [
    ...getDefaultFilterOptions(feature),
    ...alertPolicies.map((alertPolicy) => ({
      id: `alertPolicy-${alertPolicy.id}`,
      label: alertPolicy.name,
      icon: <LookoutIcon />,
      color: colors.primaryIntent,
      filter: (note: StateApiNote) => noteMatchesAlertPolicy(note, alertPolicy),
    })),
  ];
  const activeFilter = filterOptions.find((option) => option.id === filterId) ?? filterOptions[0];
  const visibleNotes = availableNotes.filter(activeFilter.filter);
  const focusedNote =
    notesState.focusedNoteId == null
      ? undefined
      : visibleNotes.find((note) => note.id === notesState.focusedNoteId);

  // Checked notes remain the target when a filter hides them. With nothing
  // checked, the action bar falls back to the change open on the map.
  const targetNotes = React.useMemo(() => {
    const checked = availableNotes.filter((note) => selectedNotes.has(note.id));
    return checked.length ? checked : focusedNote ? [focusedNote] : [];
  }, [availableNotes, selectedNotes, focusedNote]);

  const onFocusNote = React.useCallback(
    (note: StateApiNote) => {
      notesActions.focusNote(note.id);
      onSelectImageRefs(note.imageRefs);
      onSelectNote(note.id);
      if (note.geometry) {
        zoomToGeometry(note.geometry);
      }
    },
    [notesActions, onSelectImageRefs, onSelectNote, zoomToGeometry]
  );

  const toggleNoteSelected = React.useCallback((id: number | string) => {
    setSelectedNotes((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const clearTarget = React.useCallback(() => {
    setSelectedNotes(new Set());
    notesActions.focusNote(null);
  }, [notesActions]);

  const onCreateNoteFromAlert = React.useCallback(
    async (note: StateApiNote, focus: boolean) => {
      const newNote = await notesActions.createNote(
        {
          text: note.text,
          geometry: note.geometry,
          attachments: note.attachments,
          imageRefs: note.imageRefs,
          graph: note.graph,
          tagIds: note.tagIds,
          overlayFeatureCollectionIds: note.overlayFeatureCollectionIds,
          properties: note.properties,
        },
        {focus}
      );
      handleDeleteNotes(notesActions, [note], true);
      recordEvent('Created note from Lookout notification', {
        projectId: newNote.projectId,
        featureCollectionId: newNote.featureCollectionId,
        noteId: newNote.id,
      });
    },
    [notesActions]
  );

  const isSavingRef = React.useRef(false);
  const saveTarget = React.useCallback(async () => {
    // A held key repeats faster than a request completes.
    if (!targetNotes.length || !canModifyAlerts || isSavingRef.current) return;
    if (
      targetNotes.length > 1 &&
      !confirm(
        `Save ${targetNotes.length} changes as notes? They will no longer appear in Lookout.`
      )
    ) {
      return;
    }

    isSavingRef.current = true;
    let saved = 0;
    try {
      // Save sequentially so only the final note takes focus.
      for (const [index, note] of targetNotes.entries()) {
        await onCreateNoteFromAlert(note, index === targetNotes.length - 1);
        saved++;
      }
      pushNotification({
        message: saved > 1 ? `Saved ${saved} notes` : 'Saved as note',
        autoHideDuration: 3000,
        options: {intent: B.Intent.SUCCESS},
      });
      setSelectedNotes(new Set());
    } catch (error) {
      Sentry.captureException(error);
      pushNotification({
        message:
          saved > 0
            ? `Saved ${saved} of ${targetNotes.length} as notes. The rest are still in Lookout.`
            : 'Could not save as note. Please try again.',
        autoHideDuration: 5000,
        options: {intent: B.Intent.DANGER},
      });
    } finally {
      isSavingRef.current = false;
    }
  }, [targetNotes, canModifyAlerts, onCreateNoteFromAlert, pushNotification]);

  const dismissTarget = React.useCallback(() => {
    if (!targetNotes.length || !canModifyAlerts) return;
    const focusedNoteId = notesState.focusedNoteId;
    if (handleDeleteNotes(notesActions, targetNotes, false)) {
      clearTarget();
    } else if (focusedNoteId != null) {
      // The delete helper drops focus before asking for confirmation.
      notesActions.focusNote(focusedNoteId);
    }
  }, [targetNotes, canModifyAlerts, notesActions, notesState.focusedNoteId, clearTarget]);

  const moveFocus = React.useCallback(
    (offset: number) => {
      if (!visibleNotes.length) return;
      const currentIndex = visibleNotes.findIndex((note) => note.id === notesState.focusedNoteId);
      const nextIndex =
        currentIndex < 0
          ? 0
          : Math.min(Math.max(currentIndex + offset, 0), visibleNotes.length - 1);
      onFocusNote(visibleNotes[nextIndex]);
    },
    [visibleNotes, notesState.focusedNoteId, onFocusNote]
  );

  // Blueprint re-registers hotkeys when the configuration identity changes.
  // Stable configuration reads the latest handlers through this ref.
  const hotkeyHandlersRef = React.useRef({
    moveFocus,
    saveTarget,
    dismissTarget,
    toggleFocusedSelected: () => {},
  });
  React.useEffect(() => {
    hotkeyHandlersRef.current = {
      moveFocus,
      saveTarget,
      dismissTarget,
      toggleFocusedSelected: () => {
        if (canModifyAlerts && focusedNote) toggleNoteSelected(focusedNote.id);
      },
    };
  });

  const hotkeys = React.useMemo<B.HotkeyConfig[]>(() => {
    const unlessInOverlay = (handler: () => void) => (event: KeyboardEvent) => {
      // Dialogs and menus own their keypresses, including buttons and switches.
      if (event.target instanceof Element && event.target.closest('.bp5-portal')) return;
      handler();
    };
    return [
      {
        combo: 'j',
        global: true,
        label: 'Next change',
        onKeyDown: unlessInOverlay(() => hotkeyHandlersRef.current.moveFocus(1)),
      },
      {
        combo: 'k',
        global: true,
        label: 'Previous change',
        onKeyDown: unlessInOverlay(() => hotkeyHandlersRef.current.moveFocus(-1)),
      },
      {
        combo: 's',
        global: true,
        label: 'Save change as note',
        onKeyDown: unlessInOverlay(() => hotkeyHandlersRef.current.saveTarget()),
      },
      {
        combo: 'd',
        global: true,
        label: 'Dismiss change',
        onKeyDown: unlessInOverlay(() => hotkeyHandlersRef.current.dismissTarget()),
      },
      {
        combo: 'x',
        global: true,
        label: 'Check the change on the map',
        onKeyDown: unlessInOverlay(() => hotkeyHandlersRef.current.toggleFocusedSelected()),
      },
    ];
  }, []);
  B.useHotkeys(hotkeys);

  const allSelected =
    visibleNotes.length > 0 && visibleNotes.every((note) => selectedNotes.has(note.id));

  return {
    alertPolicies,
    availableNotes,
    visibleNotes,
    focusedNote,
    targetNotes,
    selectedNotes,
    allSelected,
    isBulkTarget: selectedNotes.size > 0,
    filterOptions,
    activeFilter,
    sortDirection,
    onFocusNote,
    toggleNoteSelected,
    toggleFilter: (id: string) => setFilterId(id === filterId ? ALL_FILTER_ID : id),
    toggleSortDirection: () => setSortDirection((value) => (value === 'asc' ? 'desc' : 'asc')),
    selectAll: () =>
      setSelectedNotes((previous) => {
        const next = new Set(previous);
        visibleNotes.forEach((note) => (allSelected ? next.delete(note.id) : next.add(note.id)));
        return next;
      }),
    clearTarget,
    saveTarget,
    dismissTarget,
  };
};
